//#region IMPORT
import React, { useState, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import FilterBottomSheet from '../../../Components/BottomSheet/FilterBottomSheet';
import FilterFAB from '../../../Components/Ui/FilterFAB';
import SearchBar from '../../../Components/Ui/SearchBar';
import Dropdown from '../../../Components/Ui/Dropdown';
import { BottomSheetActions } from '../../../Reducer/BottomSheetReducer';
//#endregion

function M_TCS_BASIC(props) {
  const dispatch = useDispatch();
  const bottomSheetState = useSelector((state) => state.bottomSheet);

  const [searchKeyword, setSearchKeyword] = useState('');

  const filteredList = useMemo(() => {
    let list = props.sortedWorkerList;
    if (searchKeyword.trim()) {
      const kw = searchKeyword.trim().toLowerCase();
      list = list.filter(
        (w) =>
          (w.name && w.name.toLowerCase().includes(kw)) ||
          (w.position && w.position.toLowerCase().includes(kw))
      );
    }
    return list;
  }, [props.sortedWorkerList, searchKeyword]);

  const displayCount = filteredList.length;

  const HandleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  const GetStatusBadgeClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active' || s === '정상') {
      return 'bg-primary/10 text-primary';
    }
    if (s === 'alert' || s === '경고' || s === '주의') {
      return 'bg-amber-100 text-amber-600';
    }
    return 'bg-slate-100 text-slate-600';
  };

  const GetAchievementBarClass = (rate) => {
    const r = Number(rate) || 0;
    if (r >= 100) return 'bg-primary';
    if (r < 50) return 'bg-amber-500';
    return 'bg-primary';
  };

  return (
    <div className="bg-background-light flex h-full min-h-0 flex-col">
      <SearchBar
        value={searchKeyword}
        onChange={HandleSearchChange}
        placeholder="근로자 성함 또는 직책 검색"
      />

      {/* Content */}
      <div className="flex flex-shrink-0 items-center justify-between px-4 pt-2 pb-2">
        <h2 className="text-base font-bold text-slate-900">
          근로자별 이수 내역{' '}
          <span className="text-primary ml-1">{displayCount}명</span>
        </h2>
        <Dropdown
          options={props.sortOptions || []}
          value={props.sortBy}
          onChange={props.onChangeSort}
          placeholder="정렬순"
          fullWidth={false}
          triggerClassName="flex items-center gap-1 text-xs font-medium text-slate-500"
          selectedOptionClassName="bg-primary/10 text-primary"
          menuClassName="[&_button]:text-xs"
        />
      </div>

      {/* 카드 리스트 - 가상 스크롤 */}
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
        {filteredList.length === 0 ? (
          <div className="rounded-xl border border-slate-100 bg-white p-8 text-center text-slate-500">
            {props.sortedWorkerList.length === 0
              ? '조회된 근로자가 없습니다.'
              : '검색 결과가 없습니다.'}
          </div>
        ) : (
          <Virtuoso
            className="min-h-0 flex-1"
            style={{ height: '100%' }}
            data={filteredList}
            itemContent={(_, worker) => {
              const achievementRate = Number(worker.achievementRate) || 0;
              const ratePercent = Math.min(100, Math.max(0, achievementRate));
              const statusClass = GetStatusBadgeClass(worker.status);
              const barClass = GetAchievementBarClass(achievementRate);

              return (
                <div className="pt-4">
                  <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition-all active:scale-[0.98]">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="flex size-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100">
                        <span className="text-xl font-bold text-slate-500">
                          {(worker.name || '').charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h3 className="truncate text-base font-bold text-slate-900">
                              {worker.name}
                              <span className="mx-1 font-normal text-slate-400">
                                |
                              </span>
                              <span className="text-slate-600">
                                {worker.position || '-'}
                              </span>
                            </h3>
                            <p className="mt-0.5 text-sm text-slate-500">
                              {worker.depNm || '-'}
                            </p>
                          </div>
                          <span
                            className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${statusClass}`}
                          >
                            {worker.status === 'Active' ? '달성' : '미달성'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-3">
                      {/* 달성률 */}
                      <div className="space-y-1.5">
                        <div className="flex items-end justify-between text-xs">
                          <span className="font-medium text-slate-500">
                            달성률
                          </span>
                          <span
                            className={`text-sm font-bold ${
                              ratePercent >= 100 || ratePercent >= 50
                                ? 'text-primary'
                                : 'text-amber-500'
                            }`}
                          >
                            {ratePercent}%
                          </span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                          <div
                            className={`h-full rounded-full transition-all ${barClass}`}
                            style={{ width: `${ratePercent}%` }}
                          />
                        </div>
                      </div>
                      {/* 총 누계시간 */}
                      <div className="flex items-center justify-between pt-1">
                        <div className="flex items-center gap-2">
                          <ScheduleIcon className="!text-lg text-slate-400" />
                          <span className="text-sm text-slate-500">
                            총 누계시간
                          </span>
                        </div>
                        <span className="text-sm font-bold text-slate-900">
                          {worker.totalCumulativeHours != null
                            ? `${Number(worker.totalCumulativeHours)}시간`
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }}
          />
        )}
      </div>
      {/* 바텀시트 부분: 부서(depCd), 년도(currentYear), 월(month) 선택 */}
      {bottomSheetState.type === 'filter' && (
        <FilterBottomSheet
          searchRef={props.searchRef}
          onSearch={props.SearchLogic1}
          depList={props.depList ?? []}
          tabConfig={props.tabConfig}
        />
      )}
      {/* FAB 버튼 */}
      <FilterFAB
        onFilterClick={() =>
          dispatch(BottomSheetActions.OpenBottomSheet({ type: 'filter' }))
        }
      />
    </div>
  );
}

export default M_TCS_BASIC;
